import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { armazenamento } from './armazenamento.ts';
import { useEventos, type Evento } from './eventos.ts';
import { useLessonProgress } from './lessonProgress.ts';
import { useProjectos } from './projectos.ts';
import { useIdentidade } from './identidade.ts';

/**
 * A sincronização do progresso (ADR 0009, bloco 3; RNF-013, RNF-014).
 *
 * O desenho, em três frases. Os factos nascem no dispositivo e ficam na fila
 * de `eventos.ts`, que sempre foi uma fila — só lhe faltava a marca de «já
 * foi». Os números são derivados e a autoridade é o servidor: o que ele
 * devolver substitui o que o cliente calculou, e o que ainda não foi enviado
 * soma-se por cima. Reenviar é gratuito, portanto não há estado de
 * sincronização nenhum além da marca por evento.
 *
 * Este módulo também guarda a divisão por conta (decisão 6): numa máquina do
 * laboratório, o progresso da Aida não pode ir parar à conta do Nzuzi. Cada
 * conta tem o seu espaço em `localStorage` — `<nome>:<user_id>` — e o espaço
 * anónimo é adoptado **no registo, nunca na entrada**.
 */

// ---------------------------------------------------------------------------
// Os totais autorizados
// ---------------------------------------------------------------------------

export interface Totais {
  pontos: number;
  licoesConcluidas: number;
}

interface EstadoDeSincronizacao {
  totais: Totais | null;
  definirTotais(totais: Totais): void;
}

export const useSincronizacao = create<EstadoDeSincronizacao>()(
  persist(
    (set) => ({
      totais: null,
      definirTotais: (totais) => set({ totais }),
    }),
    { name: 'sincronizacao', storage: armazenamento() },
  ),
);

/**
 * Os pontos que o ecrã mostra: os do servidor, mais o que ainda não foi
 * enviado. Sem servidor conhecido, a soma local é tudo o que há — e chega.
 */
export function pontosAExibir(eventos: readonly Evento[], totais: Totais | null): number {
  if (!totais) return eventos.reduce((s, e) => s + e.quantia, 0);
  return totais.pontos + eventos.filter((e) => !e.enviado).reduce((s, e) => s + e.quantia, 0);
}

export function porEnviar(eventos: readonly Evento[]): number {
  return eventos.filter((e) => !e.enviado).length;
}

// ---------------------------------------------------------------------------
// A descarga da fila
// ---------------------------------------------------------------------------

/** O tecto do lote é do servidor; o cliente divide e o reenvio cose o resto. */
const LOTE_MAXIMO = 500;

let aDescarregar = false;

export async function descarregarFila(): Promise<void> {
  const identidade = useIdentidade.getState().identidade;
  if (!identidade || aDescarregar) return;

  const fila = useEventos.getState().eventos.filter((e) => !e.enviado);
  const licoes = Object.entries(useLessonProgress.getState().lessons)
    .filter(([, estado]) => estado.completedAt || estado.index > 0 || estado.awarded.length > 0)
    .map(([slug, estado]) => ({
      slug,
      estado: estado.completedAt ? ('completed' as const) : ('in_progress' as const),
      // RN-007 — o que está por refazer, para o certificado ser decidido no
      // servidor sem confiar no aparelho.
      comAjuda: (estado.comAjuda ?? []).length,
    }));

  if (fila.length === 0 && licoes.length === 0) return;

  aDescarregar = true;
  try {
    for (let inicio = 0; inicio < Math.max(fila.length, 1); inicio += LOTE_MAXIMO) {
      const lote = fila.slice(inicio, inicio + LOTE_MAXIMO);
      const resposta = await fetch('/api/sincronizar', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          eventos: lote.map((e) => ({ tipo: e.tipo, origem: e.origem, dia: e.dia })),
          // As lições vão no primeiro lote; são poucas e o upsert é monótono.
          licoes: inicio === 0 ? licoes.slice(0, 200) : [],
        }),
      });
      if (!resposta.ok) return;

      const dados = (await resposta.json()) as { pontos: number; licoesConcluidas: number };
      // Marca tudo o que foi, incluindo o que o servidor não reconheceu
      // (`desconhecidos`): conteúdo renomeado não deve ser reenviado para
      // sempre. O total autorizado já não o conta, e é o total que manda.
      useEventos.getState().marcarEnviados(lote);
      useSincronizacao
        .getState()
        .definirTotais({ pontos: dados.pontos, licoesConcluidas: dados.licoesConcluidas });
    }
  } catch {
    // Sem rede. A fila fica, o indicador mostra-a, e a próxima oportunidade
    // — voltar a haver rede, abrir a aplicação, concluir uma lição — reenvia.
  } finally {
    aDescarregar = false;
  }
}

// ---------------------------------------------------------------------------
// Os espaços por conta (decisão 6)
// ---------------------------------------------------------------------------

interface ArmazemComEspaco {
  setState(parcial: object): void;
  persist: {
    setOptions(opcoes: { name: string }): void;
    rehydrate(): unknown;
    getOptions(): { name?: string };
  };
}

const ESPACOS: [ArmazemComEspaco, string, object][] = [
  [useEventos, 'eventos-pontos', { eventos: [], ultimoDia: null }],
  [useLessonProgress, 'progresso-licoes', { lessons: {} }],
  [useSincronizacao, 'sincronizacao', { totais: null }],
  // E12 — as entregas do projecto são de uma conta, como o progresso: numa
  // máquina partilhada, o feedback do tutor a um estudante não aparece ao
  // seguinte.
  [useProjectos, 'projectos', { entregas: {}, actualizadoEm: null }],
];

const CHAVE_DE_ADOPCAO = 'conta-por-adoptar';

function chave(base: string, userId: string | null): string {
  return userId ? `${base}:${userId}` : base;
}

function local(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Liga cada armazém ao espaço da conta activa e recarrega do disco.
 *
 * O estado é reposto a vazio **antes** de recarregar: num espaço novo não há
 * nada guardado, e sem a reposição o que estava em memória — o progresso de
 * outra pessoa — continuava no ecrã com o nome trocado.
 *
 * Mas só quando o espaço muda mesmo. `iniciarSincronizacao` chama isto uma
 * vez no arranque, com o `userId` que já lá está — e para quem nunca mudou de
 * conta, `chave(base, userId)` dá o mesmo nome que o armazém já tinha. Repor a
 * vazio nesse caso apaga o progresso a sério: o `persist` do zustand escreve
 * no `localStorage` a cada `setState`, portanto a reposição a vazio grava-se
 * antes de o `rehydrate()` (assíncrono) chegar a ler o que lá estava — lê de
 * volta o vazio que acabou de escrever. Foi assim que um estudante anónimo
 * perdia o progresso todo a cada vez que reabria a aplicação, mesmo sem tocar
 * em nenhuma conta.
 */
export function trocarEspaco(userId: string | null): void {
  for (const [armazem, base, vazio] of ESPACOS) {
    const alvo = chave(base, userId);
    if (armazem.persist.getOptions().name === alvo) continue;
    armazem.setState(vazio);
    armazem.persist.setOptions({ name: alvo });
    void armazem.persist.rehydrate();
  }
}

/** Chamado no registo: o percurso anónimo deste navegador pertence a quem o fez. */
export function marcarParaAdopcao(email: string): void {
  local()?.setItem(CHAVE_DE_ADOPCAO, email.toLowerCase());
}

/**
 * A adopção acontece quando o dono do registo entra pela primeira vez — é o
 * momento em que o `user_id` passa a ser conhecido. Entrar com **outra**
 * conta não adopta nada: numa máquina partilhada, o que cá estava não é de
 * quem chega (decisão 6, e é a regra inteira).
 */
function adoptarSeForOMesmo(email: string, userId: string): void {
  const armazem = local();
  if (!armazem || armazem.getItem(CHAVE_DE_ADOPCAO) !== email.toLowerCase()) return;

  for (const [, base] of ESPACOS) {
    const anonimo = armazem.getItem(base);
    if (anonimo !== null && armazem.getItem(chave(base, userId)) === null) {
      armazem.setItem(chave(base, userId), anonimo);
    }
    armazem.removeItem(base);
  }
  armazem.removeItem(CHAVE_DE_ADOPCAO);
}

// ---------------------------------------------------------------------------
// O arranque
// ---------------------------------------------------------------------------

let iniciada = false;

/**
 * Chamado uma vez pela casca, depois da primeira pintura. Daqui em diante a
 * sincronização é dirigida por acontecimentos: identidade que muda, rede que
 * volta, eventos novos na fila.
 */
export function iniciarSincronizacao(): void {
  if (iniciada) return;
  iniciada = true;

  trocarEspaco(useIdentidade.getState().identidade?.userId ?? null);

  useIdentidade.subscribe((estado, anterior) => {
    const novo = estado.identidade;
    if (novo?.userId === anterior.identidade?.userId) return;
    if (novo) adoptarSeForOMesmo(novo.email, novo.userId);
    trocarEspaco(novo?.userId ?? null);
    if (novo) void descarregarFila();
  });

  // Um evento novo na fila agenda uma descarga. O atraso agrega a rajada de
  // uma lição inteira num pedido só.
  let temporizador: ReturnType<typeof setTimeout> | undefined;
  useEventos.subscribe((estado, anterior) => {
    if (estado.eventos === anterior.eventos) return;
    if (estado.eventos.some((e) => !e.enviado)) {
      clearTimeout(temporizador);
      temporizador = setTimeout(() => void descarregarFila(), 2000);
    }
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => void descarregarFila());
  }

  void descarregarFila();
}
